/**
 * Clipboard Behavior
 *
 * Handles clipboard operations:
 * - Mod+C: Copy selected nodes and edges
 * - Mod+X: Cut (copy then delete)
 * - Mod+V: Paste at cursor position
 * - Mod+D: Duplicate in place
 *
 * External clipboard support:
 * - Copies both human-readable text and internal JSON format
 * - Pastes external text as text nodes
 * - Pastes external images as image nodes
 */

import type { ClipboardBehaviorContext, ClipboardData } from './types';
import * as viewState from '../view-state';

/** Callbacks for external content creation (set during behavior registration) */
let onExternalPasteFn:
	| ((data: { type: 'image'; blob: Blob; position: { x: number; y: number } } | { type: 'text'; text: string; position: { x: number; y: number } }) => void)
	| undefined;
let createTextNodeFn: ((text: string, position: { x: number; y: number }) => string) | undefined;
let createImageNodeFn:
	| ((imageData: string | Blob, position: { x: number; y: number }) => string)
	| undefined;
let getNodeLabelFn: ((nodeId: string) => string | undefined) | undefined;
let getNodeImageBlobFn: ((nodeId: string) => Promise<Blob | null>) | undefined;

/** Internal clipboard storage (for same-session full-fidelity paste) */
let internalClipboard: ClipboardData | null = null;

/** The plain text we last wrote to clipboard (to detect if paste is from us) */
let lastWrittenText: string | null = null;

/** Timestamp of last paste operation (to prevent double-paste) */
let lastPasteTime: number = 0;

/** Timestamp of last copy operation (to prevent double-copy) */
let lastCopyTime: number = 0;

/**
 * Register clipboard behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerClipboardBehavior(ctx: ClipboardBehaviorContext): () => void {
	const {
		input,
		getViewState,
		updateViewState,
		getNodes,
		getEdges,
		getNodeData,
		pasteNodes,
		pasteEdges,
		deleteSelection,
		getCursorPosition,
		createTextNode,
		createImageNode,
		getNodeLabel,
		getNodeImageBlob,
		log,
	} = ctx;
	const unsubscribers: (() => void)[] = [];

	// Store external content callbacks for use in helper functions
	onExternalPasteFn = ctx.onExternalPaste;
	createTextNodeFn = createTextNode;
	createImageNodeFn = createImageNode;
	getNodeLabelFn = getNodeLabel;
	getNodeImageBlobFn = getNodeImageBlob;

	// =========================================================================
	// Copy (Mod+C)
	// =========================================================================

	unsubscribers.push(
		input.on(
			'keydown',
			'mod+c',
			{
				mode: 'default',
				command: { id: 'clipboard.copy', name: 'Copy' },
				category: 'Edit',
			},
			async (inputCtx) => {
				// Debounce: prevent double-copy within 100ms
				const now = Date.now();
				if (now - lastCopyTime < 100) {
					return;
				}
				lastCopyTime = now;

				inputCtx.stopPropagation();
				inputCtx.event?.preventDefault?.();

				const state = getViewState();
				const selectedIds = viewState.getSelectedIds(state);
				log?.('copy-start', `selectedIds: ${selectedIds.join(', ')}`);
				if (selectedIds.length === 0) {
					log?.('copy-empty', 'No selection');
					return;
				}

				const allNodes = getNodes();
				log?.('copy-nodes', `getNodes returned ${allNodes.length} nodes: ${allNodes.map(n => n.id).join(', ')}`);

				const clipboardData = buildClipboardData(selectedIds, getNodes, getEdges, getNodeData);
				if (!clipboardData) {
					log?.('copy-failed', 'buildClipboardData returned null');
					return;
				}

				await writeToClipboard(clipboardData, getNodeLabel);
				log?.('copy', `${clipboardData.nodes.length} nodes, ${clipboardData.edges.length} edges`);
			}
		)
	);

	// =========================================================================
	// Cut (Mod+X)
	// =========================================================================

	unsubscribers.push(
		input.on(
			'keydown',
			'mod+x',
			{
				mode: 'default',
				command: { id: 'clipboard.cut', name: 'Cut' },
				category: 'Edit',
			},
			async (inputCtx) => {
				inputCtx.stopPropagation();
				inputCtx.event?.preventDefault?.();

				const state = getViewState();
				const selectedIds = viewState.getSelectedIds(state);
				if (selectedIds.length === 0) return;

				const clipboardData = buildClipboardData(selectedIds, getNodes, getEdges, getNodeData);
				if (!clipboardData) return;

				await writeToClipboard(clipboardData, getNodeLabel);

				// Delete selection after copy
				const nodeIds = clipboardData.nodes.map((n) => n.id);
				const edgeIds = clipboardData.edges.map((e) => e.id);
				deleteSelection(nodeIds, edgeIds);
				updateViewState(viewState.clearSelection(state));

				log?.('cut', `${nodeIds.length} nodes, ${edgeIds.length} edges`);
			}
		)
	);

	// =========================================================================
	// Paste (Mod+V)
	// =========================================================================

	unsubscribers.push(
		input.on(
			'keydown',
			'mod+v',
			{
				mode: 'default',
				command: { id: 'clipboard.paste', name: 'Paste' },
				category: 'Edit',
			},
			async (inputCtx) => {
				inputCtx.stopPropagation();
				inputCtx.event?.preventDefault?.();

				// Prevent double-paste (can happen if handler fires twice)
				const now = Date.now();
				if (now - lastPasteTime < 100) {
					return;
				}
				lastPasteTime = now;

				log?.('paste-start', `internalClipboard: ${internalClipboard ? internalClipboard.nodes.length + ' nodes' : 'null'}`);

				const cursorPos = getCursorPosition();
				log?.('paste-cursor', `(${cursorPos.x.toFixed(0)}, ${cursorPos.y.toFixed(0)})`);

				// Try to read clipboard content (handles internal JSON, external text, images)
				const result = await readFromClipboard(cursorPos, updateViewState, getViewState, log);
				log?.('paste-result', `type: ${result.type}`);

				if (result.type === 'internal') {
					// Internal clipboard data - paste nodes and edges
					log?.('paste-internal', `${result.data.nodes.length} nodes to paste`);
					// pasteNodes can be async (e.g., for SDK persistence)
					const nodeIdMap = await Promise.resolve(pasteNodes(result.data.nodes, cursorPos));
					log?.('paste-created', `nodeIdMap size: ${nodeIdMap.size}`);
					pasteEdges(result.data.edges, nodeIdMap);

					// Select the newly pasted nodes
					const newNodeIds = Array.from(nodeIdMap.values());
					updateViewState(viewState.applyMultiSelection(getViewState(), newNodeIds, 'replace'));

					log?.('paste', `${newNodeIds.length} nodes at (${cursorPos.x.toFixed(0)}, ${cursorPos.y.toFixed(0)})`);
				} else if (result.type === 'external-handled') {
					// External content was handled (text or image node created)
					// Selection was already updated by readFromClipboard
				} else {
					log?.('paste-empty', 'No valid clipboard data');
				}
			}
		)
	);

	// =========================================================================
	// Duplicate in Place (Mod+D)
	// =========================================================================

	unsubscribers.push(
		input.on(
			'keydown',
			'mod+d',
			{
				mode: 'default',
				command: { id: 'clipboard.duplicate', name: 'Duplicate' },
				category: 'Edit',
			},
			async (inputCtx) => {
				inputCtx.stopPropagation();
				inputCtx.event?.preventDefault?.();

				const state = getViewState();
				const selectedIds = viewState.getSelectedIds(state);
				if (selectedIds.length === 0) return;

				const clipboardData = buildClipboardData(selectedIds, getNodes, getEdges, getNodeData);
				if (!clipboardData) return;

				// Calculate center of selection
				const nodes = getNodes();
				const selectedNodes = nodes.filter((n) => selectedIds.includes(n.id));
				const center = calculateSelectionCenter(selectedNodes);

				// Offset the paste position slightly
				const offset = { x: 20, y: 20 };
				const pastePos = { x: center.x + offset.x, y: center.y + offset.y };

				// pasteNodes can be async (e.g., for SDK persistence)
				const nodeIdMap = await Promise.resolve(pasteNodes(clipboardData.nodes, pastePos));
				pasteEdges(clipboardData.edges, nodeIdMap);

				// Select the newly duplicated nodes
				const newNodeIds = Array.from(nodeIdMap.values());
				updateViewState(viewState.applyMultiSelection(getViewState(), newNodeIds, 'replace'));

				log?.('duplicate', `${newNodeIds.length} nodes`);
			}
		)
	);

	// =========================================================================
	// Cleanup
	// =========================================================================

	return () => {
		for (const unsub of unsubscribers) {
			unsub();
		}
	};
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Build clipboard data from selected nodes and edges.
 */
function buildClipboardData(
	selectedIds: string[],
	getNodes: () => Array<{ id: string; position: { x: number; y: number }; size: { width: number; height: number } }>,
	getEdges: () => Array<{ id: string; sourceNodeId: string; targetNodeId: string }>,
	getNodeData: (nodeId: string) => { type: string; data?: unknown } | undefined
): ClipboardData | null {
	const nodes = getNodes();
	const edges = getEdges();
	const selectedNodeIds = new Set<string>();

	// Find selected nodes
	const selectedNodes = nodes.filter((n) => {
		if (selectedIds.includes(n.id)) {
			selectedNodeIds.add(n.id);
			return true;
		}
		return false;
	});

	if (selectedNodes.length === 0) return null;

	// Calculate selection center
	const center = calculateSelectionCenter(selectedNodes);

	// Build node data with relative positions
	// Note: relativePosition is from the NODE CENTER to the SELECTION CENTER
	// This allows paste to position nodes correctly around the cursor
	const clipboardNodes: ClipboardData['nodes'] = selectedNodes.map((node) => {
		const nodeData = getNodeData(node.id);
		// Calculate node center (position is top-left)
		const nodeCenter = {
			x: node.position.x + node.size.width / 2,
			y: node.position.y + node.size.height / 2,
		};
		return {
			id: node.id,
			type: nodeData?.type ?? 'unknown',
			relativePosition: {
				x: nodeCenter.x - center.x,
				y: nodeCenter.y - center.y,
			},
			size: { ...node.size },
			data: nodeData?.data,
		};
	});

	// Find edges where both endpoints are in selected nodes
	const selectedEdgeIds = new Set(selectedIds);
	const clipboardEdges: ClipboardData['edges'] = edges
		.filter((e) => {
			// Edge is included if:
			// 1. It was explicitly selected, OR
			// 2. Both its source and target are selected nodes
			const bothEndpointsSelected =
				selectedNodeIds.has(e.sourceNodeId) && selectedNodeIds.has(e.targetNodeId);
			return selectedEdgeIds.has(e.id) || bothEndpointsSelected;
		})
		.filter((e) => {
			// But only keep edges where both endpoints are in the copied nodes
			// (in case an edge was selected but its nodes weren't)
			return selectedNodeIds.has(e.sourceNodeId) && selectedNodeIds.has(e.targetNodeId);
		})
		.map((e) => ({
			id: e.id,
			sourceNodeId: e.sourceNodeId,
			targetNodeId: e.targetNodeId,
		}));

	return {
		version: 1,
		nodes: clipboardNodes,
		edges: clipboardEdges,
	};
}

/**
 * Calculate the center point of selected nodes.
 * Note: node.position is the TOP-LEFT corner.
 */
function calculateSelectionCenter(
	nodes: Array<{ position: { x: number; y: number }; size: { width: number; height: number } }>
): { x: number; y: number } {
	if (nodes.length === 0) return { x: 0, y: 0 };

	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	for (const node of nodes) {
		// Node position is top-left corner
		const left = node.position.x;
		const right = node.position.x + node.size.width;
		const top = node.position.y;
		const bottom = node.position.y + node.size.height;

		minX = Math.min(minX, left);
		maxX = Math.max(maxX, right);
		minY = Math.min(minY, top);
		maxY = Math.max(maxY, bottom);
	}

	return {
		x: (minX + maxX) / 2,
		y: (minY + maxY) / 2,
	};
}

/**
 * Write clipboard data to the system clipboard.
 * Writes clean human-readable text for external use.
 * Stores full data internally for same-session full-fidelity paste.
 * If a single image node is copied and getNodeImageBlob is available,
 * also writes the image to the clipboard.
 */
async function writeToClipboard(
	data: ClipboardData,
	getNodeLabel?: (nodeId: string) => string | undefined
): Promise<void> {
	// Store full data internally for same-session paste
	internalClipboard = data;

	// Build clean human-readable text
	const humanReadable = buildHumanReadableText(data, getNodeLabel);

	// Remember what we wrote so we can detect our own paste
	lastWrittenText = humanReadable;

	try {
		// Check if we're copying a single image node
		if (data.nodes.length === 1 && data.nodes[0].type === 'image' && getNodeImageBlobFn) {
			const imageBlob = await getNodeImageBlobFn(data.nodes[0].id);
			if (imageBlob && navigator.clipboard?.write) {
				// Write both image and text to clipboard
				// Note: ClipboardItem requires blobs, not strings
				try {
					const clipboardItem = new ClipboardItem({
						'image/png': imageBlob,
						'text/plain': new Blob([humanReadable], { type: 'text/plain' }),
					});
					await navigator.clipboard.write([clipboardItem]);
					return;
				} catch {
					// Fall through to text-only write
				}
			}
		}

		// Default: write clean text to system clipboard
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(humanReadable);
		}
	} catch {
		// Clipboard API may fail (permissions, non-secure context, etc.)
		// Internal clipboard is our fallback
	}
}

/**
 * Build human-readable text representation of clipboard data.
 * Just node labels/content with logical paragraph separation.
 */
function buildHumanReadableText(
	data: ClipboardData,
	getNodeLabel?: (nodeId: string) => string | undefined
): string {
	const labels: string[] = [];

	for (const node of data.nodes) {
		// Try to get a human-readable label, fall back to type
		const label = getNodeLabel?.(node.id) ?? node.type;
		if (label) {
			labels.push(label);
		}
	}

	// Join with double newline for paragraph separation
	return labels.join('\n\n');
}

/** Result of reading from clipboard */
type ClipboardReadResult =
	| { type: 'internal'; data: ClipboardData }
	| { type: 'external-handled' }
	| { type: 'empty' };

/**
 * Read clipboard data from the system clipboard.
 * If clipboard matches what we wrote, use internal full-fidelity data.
 * Otherwise treat as external content (text or image).
 */
async function readFromClipboard(
	cursorPos: { x: number; y: number },
	updateViewState: (state: import('../types').ViewState) => void,
	getViewState: () => import('../types').ViewState,
	log?: (type: string, detail: string) => void
): Promise<ClipboardReadResult> {
	// FIRST: Check if this is an internal paste by comparing clipboard text
	// This must happen BEFORE checking for images, because internal image copies
	// also write image data to the clipboard, but we want to preserve the node size
	try {
		if (navigator.clipboard?.readText) {
			const text = await navigator.clipboard.readText();
			log?.('read-clipboard-text', `"${text.slice(0, 50)}${text.length > 50 ? '...' : ''}"`);

			// Normalize text for comparison (handle line ending differences)
			const normalizedText = text.trim().replace(/\r\n/g, '\n');
			const normalizedLastWritten = lastWrittenText?.trim().replace(/\r\n/g, '\n');
			log?.('read-clipboard-compare', `match: ${normalizedText === normalizedLastWritten}, lastWritten: "${normalizedLastWritten?.slice(0, 50) ?? 'null'}"`);

			// If text matches what we wrote, use internal full-fidelity data
			if (normalizedText === normalizedLastWritten && internalClipboard) {
				log?.('read-clipboard-internal', 'Using internal clipboard');
				return { type: 'internal', data: internalClipboard };
			}
		}
	} catch (e) {
		log?.('read-clipboard-error', `Text read failed: ${e}`);
		// Text read failed, will try other methods
	}

	// SECOND: Try to check for external images using the read() API
	try {
		if (navigator.clipboard?.read && (onExternalPasteFn || createImageNodeFn)) {
			const clipboardItems = await navigator.clipboard.read();
			for (const item of clipboardItems) {
				const imageType = item.types.find((t) => t.startsWith('image/'));
				if (imageType) {
					const blob = await item.getType(imageType);
					if (onExternalPasteFn) {
						onExternalPasteFn({ type: 'image', blob, position: cursorPos });
					} else if (createImageNodeFn) {
						const newNodeId = createImageNodeFn(blob, cursorPos);
						updateViewState(viewState.applyMultiSelection(getViewState(), [newNodeId], 'replace'));
					}
					log?.('paste-image', `Image pasted at (${cursorPos.x.toFixed(0)}, ${cursorPos.y.toFixed(0)})`);
					return { type: 'external-handled' };
				}
			}
		}
	} catch {
		// Image read failed - this is common (permissions, no image data, etc.)
	}

	// THIRD: Handle external text (text that doesn't match our last write)
	try {
		if (navigator.clipboard?.readText) {
			const text = await navigator.clipboard.readText();

			if (text.trim()) {
				if (onExternalPasteFn) {
					onExternalPasteFn({ type: 'text', text, position: cursorPos });
					log?.('paste-text', `Text pasted at (${cursorPos.x.toFixed(0)}, ${cursorPos.y.toFixed(0)})`);
					return { type: 'external-handled' };
				} else if (createTextNodeFn) {
					const newNodeId = createTextNodeFn(text, cursorPos);
					updateViewState(viewState.applyMultiSelection(getViewState(), [newNodeId], 'replace'));
					log?.('paste-text', `Text node created at (${cursorPos.x.toFixed(0)}, ${cursorPos.y.toFixed(0)})`);
					return { type: 'external-handled' };
				}
			}
		}
	} catch {
		// System clipboard read failed
	}

	// Fall back to internal clipboard (same session, clipboard API failed)
	if (internalClipboard) {
		return { type: 'internal', data: internalClipboard };
	}

	return { type: 'empty' };
}

/**
 * Validate that data matches our clipboard format.
 */
function isValidClipboardData(data: unknown): data is ClipboardData {
	if (typeof data !== 'object' || data === null) return false;
	const obj = data as Record<string, unknown>;

	if (obj.version !== 1) return false;
	if (!Array.isArray(obj.nodes)) return false;
	if (!Array.isArray(obj.edges)) return false;

	// Basic structure validation
	for (const node of obj.nodes) {
		if (typeof node !== 'object' || node === null) return false;
		const n = node as Record<string, unknown>;
		if (typeof n.id !== 'string') return false;
		if (typeof n.type !== 'string') return false;
		if (typeof n.relativePosition !== 'object') return false;
	}

	return true;
}
